import type { components as componentsTagId } from 'topvisor-openapi/src/ts/Tags_2/Types/TagId';
import { genFieldFilter } from './field';

type TagId = componentsTagId['schemas']['Tags_2.Types.TagId'];
type TagIdExclude = `-${TagId}`;

type DataWithModifierOperator<Model> = {
	[K in keyof Model]: Model[K] | Model[K][] | Set<Model[K]> | string
};

/**
 * Модификатор оператора сравнения
 *
 * Используется посредством добавления к строковому значению поля для упрощения синтаксиса указания фильтров
 *
 * @example Поиск вхождения `Иван` в поле `name`
 * genFilters({
 *     id: 12345, // без модификатора опертора
 *     name: '%:' + 'Иван', // с модификатором опертора
 * });
 */
type ModifierOperator = '>=' | '<=' | '!=' | '>' | '<' | '%' | '!%' | '^' | '~' | '!~' | 'null' | '!null';

type ValueWithModifierOperator = `${ModifierOperator}:${string | number}`;

/**
 * Сгенерировать массив с фильтрами API
 *
 * Ключи - `filedName`, значения - `[ModifierOperator:]fieldValue` / `fieldValues`
 *
 * Значения `undefined` будут пропускаться
 *
 * Додерживает типы:
 *
 * - string, number, boolean - строгое равенство `EQUALS`, если не указан `modifierOperator`
 * - Array - вхождение `IN`
 * - Set<string | number> - вхождение `IN`
 *
 * @see ModifierOperator
 */
export const genFilters = <Model extends Api.Ext.Schemas['Model']>(
	dataWithModifierOperator: DataWithModifierOperator<Model>,
): Api.Field.Filter<Model>[] => {
	const filters: Api.Field.Filter<Model>[] = [];

	Object.keys(dataWithModifierOperator).forEach(_name => {
		const name = _name as string & keyof Model;
		const value = dataWithModifierOperator[name];
		if (value === undefined) return;

		let filter: Api.Field.Filter<Model> | undefined;

		if (name === 'tags_ids' || name === 'tags') {
			pushFilterTagsIds(name, filters, value as any);
		} else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
			filter = genFilterPrimitive(name, value);
		} else if (Array.isArray(value) || value instanceof Set) {
			filter = genFilterIn(name, value as any);
		}

		if (filter) {
			filters.push(filter);
		}
	});

	return filters;
};

/**
 * Получить value и operator из valueWithModifierOperator
 */
const extractOperator = (valueWithModifierOperator: ValueWithModifierOperator): {
	value: any,
	operator: Api.Ext.Schemas['FieldOperator']
} => {
	let value: string | number = valueWithModifierOperator;
	let operator: Api.Field.Filter<any>['operator'] = 'EQUALS';

	let modifierOperator: ModifierOperator | undefined;
	if (valueWithModifierOperator[1] === ':' || valueWithModifierOperator[2] === ':') {
		[modifierOperator, value] = valueWithModifierOperator.split(':') as [ModifierOperator, string];
	}

	switch (modifierOperator) {
		case '>':
			operator = 'GREATER_THAN';
			value = Number(value);

			break;
		case '>=':
			operator = 'GREATER_THAN_EQUALS';
			value = Number(value);

			break;
		case '<':
			operator = 'LESS_THAN';
			value = Number(value);

			break;
		case '<=':
			operator = 'LESS_THAN_EQUALS';
			value = Number(value);

			break;
		case '!=':
			operator = 'NOT_EQUALS';

			if (value.match(/^\d+$/)) value = Number(value);

			break;
		case '%':
			operator = 'CONTAINS';

			break;
		case '!%':
			operator = 'DOES_NOT_CONTAIN';

			break;
		case '^':
			operator = 'STARTS_WITH';

			break;
		case 'null':
			operator = 'IS_NULL';

			break;
		case '!null':
			operator = 'IS_NOT_NULL';

			break;
		case '~':
			operator = 'REGEXP';

			break;
		case '!~':
			operator = 'NOT_REGEXP';

			break;
	}

	return { value, operator };
};

/**
 * Сгенерировать и добавить API фильтр из списка тегов
 */
export const pushFilterTagsIds = <
	Model extends Api.Ext.Schemas['Model'] = any,
	FieldName extends Api.Field.Name<Model> = Api.Field.Name<Model>
>(
	name: FieldName,
	filters: Api.Field.Filter<Model, FieldName>[],
	tagsIds: Array<TagId | TagIdExclude>,
) => {
	const tagsIncluded: Array<Model[FieldName] & TagId> = [];
	const tagsExcluded: Array<Model[FieldName] & TagId> = [];

	tagsIds.forEach((tagId) => {
		if (tagId[0] === '-') {
			tagsExcluded.push(tagId.substring(1) as Model[FieldName] & TagId);
		} else {
			tagsIncluded.push(tagId as Model[FieldName] & TagId);
		}
	});

	if (tagsIncluded.length) filters.push(genFieldFilter<Model, FieldName>(name, 'IN', tagsIncluded));
	if (tagsExcluded.length) filters.push(genFieldFilter<Model, FieldName>(name, 'NOT_IN', tagsExcluded));
};

/**
 * Сгенерировать API фильтр из простого значения
 */
export const genFilterPrimitive = <
	Model extends Api.Ext.Schemas['Model'] = any,
	FieldName extends Api.Field.Name<Model> = Api.Field.Name<Model>
>(
	name: FieldName,
	valueWithModifierOperator: number | string | boolean | ValueWithModifierOperator,
) => {
	let value = valueWithModifierOperator;
	let operator: Api.Field.Filter<any>['operator'] = 'EQUALS';

	if (typeof valueWithModifierOperator === 'string') {
		let {} = { value, operator } = extractOperator(valueWithModifierOperator as ValueWithModifierOperator);
	}

	// @deprecated
	if (typeof value === 'boolean') {
		value = value ? 1 : 0;
	}

	return genFieldFilter<Model>(name, operator, [value as any]);
};

/**
 * Сгенерировать API фильтр IN
 */
export const genFilterIn = <
	Model extends Api.Ext.Schemas['Model'] = any,
	FieldName extends Api.Field.Name<Model> = Api.Field.Name<Model>
>(
	name: FieldName,
	valuesOrSet: Model[FieldName][] | Set<Model[FieldName]>,
) => {
	return genFieldFilter<Model, FieldName>(name, 'IN', Array.isArray(valuesOrSet) ? valuesOrSet : [...valuesOrSet]);
};
